import { Box, Paper, Alert, Button, Chip } from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi, type DxCriticalAlertDto } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { formatDateTime } from "@/shared/datetime";

export function CriticalValuesPage() {
  const qc = useQueryClient();
  const canAck = useAuth(s => s.hasPermission("diagnostics.critical.acknowledge"));
  const query = useQuery({ queryKey: ["dx-critical"], queryFn: () => diagnosticsApi.listPendingCriticalValues() });

  const ack = useMutation({
    meta: { successMessage: "Critical value acknowledged" },
    mutationFn: (id: string) => diagnosticsApi.acknowledgeCriticalValue(id, { note: "Acknowledged from worklist" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dx-critical"] }),
  });

  const columns: GridColDef<DxCriticalAlertDto>[] = [
    { field: "analyte", headerName: "Analyte", width: 160 },
    { field: "value", headerName: "Value", width: 110, align: "right", headerAlign: "right" },
    { field: "flag", headerName: "Flag", width: 130, renderCell: p => <Chip size="small" color="error" label={p.row.flag} /> },
    { field: "raisedAt", headerName: "Raised", width: 200, valueGetter: v => v ? formatDateTime(v as string) : "" },
    {
      field: "actions", headerName: "", flex: 1, minWidth: 160, sortable: false, align: "right",
      renderCell: p => canAck ? <Button size="small" variant="outlined" onClick={() => ack.mutate(p.row.id)}>Acknowledge</Button> : null,
    },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Diagnostics"]} title="Critical values" />
      <Paper sx={{ p: 2 }}>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {ack.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(ack.error)}</Alert>}
        <Box sx={{ height: 540 }}>
          <DataGrid rows={query.data ?? []} columns={columns} loading={query.isLoading} getRowId={r => r.id}
            disableRowSelectionOnClick pageSizeOptions={[25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} />
        </Box>
      </Paper>
    </Box>
  );
}
